import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Card, CardContent } from './ui/card';
import { getRouteNameFromUrl } from '../utils/urlUtils';

interface LeadFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialService?: string;
}

export function LeadFormDialog({ isOpen, onClose, initialService }: LeadFormDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    service: initialService || '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Update service if initialService changes
  useEffect(() => {
    if (initialService) {
      setFormData(prev => ({ ...prev, service: initialService }));
    }
  }, [initialService]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const currentRoute = getRouteNameFromUrl();
      
      // Prepare the API payload
      const payload = {
        Name: formData.name,
        Email: formData.email,
        Company: formData.company,
        Phone: formData.mobile,
        ServiceInterest: formData.service,
        Message: formData.message,
        SourcePage: currentRoute
      };

      console.log('Submitting lead form data:', payload);

      // Make the API call
      const response = await fetch('https://8d9688175990e732b64b23a8cce6c4.d1.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/6258e9db2478490795dbf96069308240/triggers/manual/paths/invoke/?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=Ruo_X_h6JXHAEmoPtdEKWsvFhWAieuImsxOrp7LuKxk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitStatus('success');
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          mobile: '',
          company: '',
          service: '',
          message: ''
        });

        // Close dialog after 2 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error submitting lead form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center p-4 z-[100]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            className="relative w-full max-w-lg mx-auto"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-[#052A44] border-white/10 shadow-2xl">
              <CardContent className="p-8 relative">
                {/* Close Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>

                {/* Header */}
                <div className="text-center mb-6">
                  <motion.div
                    className="inline-flex items-center space-x-2 bg-[#00A8E8]/10 px-4 py-2 rounded-full mb-4 border border-[#00A8E8]/20"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Sparkles className="h-4 w-4 text-[#00A8E8]" />
                    <span className="text-sm text-[#00A8E8] font-medium">Get Started</span>
                  </motion.div>
                  
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Let's Build Something Great
                  </h2>
                  <p className="text-white/60">
                    Tell us about your project and we'll get back to you within 24 hours.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-white">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="mt-1 bg-white/5 border-white/10 text-white focus:border-[#00A8E8]"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-white">Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="mt-1 bg-white/5 border-white/10 text-white focus:border-[#00A8E8]"
                        placeholder="Acme Inc."
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-white">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-1 bg-white/5 border-white/10 text-white focus:border-[#00A8E8]"
                        placeholder="john@acme.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile" className="text-white">Phone</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        className="mt-1 bg-white/5 border-white/10 text-white focus:border-[#00A8E8]"
                        placeholder="+1 (555) 000-0000"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-white">I'm interested in...</Label>
                    <Select 
                      value={formData.service} 
                      onValueChange={(value) => handleInputChange('service', value)}
                    >
                      <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white focus:border-[#00A8E8]">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Enterprise AI">Enterprise AI Strategy</SelectItem>
                        <SelectItem value="Small Business">Small Business Solutions</SelectItem>
                        <SelectItem value="Brainsie Platform">Brainsie Platform</SelectItem>
                        <SelectItem value="Consulting">General Consulting</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="mt-1 bg-white/5 border-white/10 text-white focus:border-[#00A8E8] min-h-[100px]"
                      placeholder="Tell us more about your needs..."
                    />
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center text-green-400 text-sm p-3 bg-green-400/10 rounded-lg border border-green-400/20"
                    >
                      ✓ Thank you! We'll be in touch shortly.
                    </motion.div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center text-red-400 text-sm p-3 bg-red-400/10 rounded-lg border border-red-400/20"
                    >
                      ⚠ Something went wrong. Please try again.
                    </motion.div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#DBA328] hover:bg-[#DBA328]/90 text-black font-semibold py-6 text-lg rounded-lg mt-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : submitStatus === 'success' ? (
                      'Sent Successfully'
                    ) : (
                      'Get Started'
                    )}
                  </Button>
                </form>

                <p className="text-xs text-white/40 text-center mt-4">
                  Your data is secure. We never spam.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof window !== 'undefined' ? createPortal(dialogContent, document.body) : null;
}
